import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MdVerified } from 'react-icons/md'
import { useAuth } from '@/hooks/useAuth'
import { useCustomVerification } from '@/hooks/useCustomVerification'
import { useVerificationPreferences } from '@/hooks/useVerificationPreferences'
import { useBatchVerificationContext } from '@/contexts/BatchVerificationContext'
import { fetchConstellationVerifications } from '@/services/verificationService'
import { VERIFICATION_STALE_TIME } from '@/constants/verification'
import { VerificationExplanationModal } from '@/components/VerificationExplanationModal'

interface VerificationBadgeProps {
    nativeVerified?: boolean
    authorDid?: string
    authorHandle?: string
    authorDisplayName?: string
    isViaBridge?: boolean
    size?: 'sm' | 'md' | 'lg'
}

const SIZE_CLASSES = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
} as const

export function VerificationBadge({
    nativeVerified,
    authorDid,
    authorHandle,
    authorDisplayName,
    isViaBridge = false,
    size = 'md',
}: VerificationBadgeProps) {
    const [showModal, setShowModal] = useState(false)
    const { session } = useAuth()
    const { preferences } = useVerificationPreferences()

    // When inside a PostList, a BatchVerificationProvider supplies pre-fetched results.
    // When on a standalone page (e.g., ViewProfile), the context is null and we fall
    // back to the individual useCustomVerification hook below.
    const batchContext = useBatchVerificationContext()

    // Individual hook is disabled when batch context is present (DID passed as undefined).
    const individualVerification = useCustomVerification(
        batchContext ? undefined : authorDid,
        batchContext ? undefined : authorHandle,
        batchContext ? undefined : authorDisplayName
    )

    // Resolve verification from batch context or individual hook.
    let customVerification = individualVerification
    if (batchContext && authorDid) {
        const batchResult = batchContext.getVerification(authorDid)
        customVerification = {
            isVerified: batchResult?.isVerified ?? false,
            verifications: batchResult?.verifiers ?? [],
            isLoading: batchContext.isLoading,
        }
    }

    // Only check "have I verified this person?" for AT Protocol DIDs — Constellation
    // tracks AT Protocol records only, so AP handles can't be looked up here.
    const isAtDid = !!authorDid?.startsWith('did:')

    // Subscribe to the myVerification cache for this author. On profile pages
    // the header's useMyVerification already populates this cache (including
    // optimistic updates after verify/revoke), so badges on posts reactively
    // pick up the same value with no extra network requests.
    const { data: myVerificationRkey } = useQuery({
        queryKey: ['myVerification', session?.did, authorDid],
        queryFn: async () => {
            const links = await fetchConstellationVerifications(authorDid!, [session!.did])
            return links.length > 0 ? links[0].rkey : null
        },
        enabled:
            !batchContext &&
            !preferences.hideAll &&
            preferences.enabled &&
            !!session?.did &&
            !!authorDid &&
            isAtDid &&
            session.did !== authorDid,
        staleTime: VERIFICATION_STALE_TIME,
    })

    if (preferences.hideAll) return null

    // Genuine boolean-OR: show the badge if ANY source says verified. ?? would short-circuit
    // on a `false` nativeVerified and never check the other sources.
    /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
    const showBadge =
        nativeVerified ||
        customVerification.isVerified ||
        (preferences.enabled && !!myVerificationRkey)
    /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
    if (!showBadge) return null

    return (
        <>
            <button
                onClick={e => {
                    e.stopPropagation()
                    setShowModal(true)
                }}
                className="flex-shrink-0 focus:outline-none hover:opacity-80 transition-opacity"
                title="Verified account"
                aria-label="Verified account"
            >
                <MdVerified className={`text-blue-500 ${SIZE_CLASSES[size]}`} />
            </button>
            <VerificationExplanationModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                isViaBridge={isViaBridge}
                customVerifications={
                    customVerification.isVerified ? customVerification.verifications : undefined
                }
                isCustomVerification={customVerification.isVerified && !nativeVerified}
                isVerifiedByYou={preferences.enabled && !!myVerificationRkey}
            />
        </>
    )
}
